import { useEffect, useState } from 'react';
import {
  LatencyOverTimeChart,
  ModelBreakdownTable,
  OutcomeChart,
  PercentileChart,
  ThroughputChart,
} from '../apigen/stress/StressAnalyticsPanels';
import { fetchQaRunAnalytics } from './api';
import { formatCount, formatSeconds, formatUsd, runCostLabel } from './format';
import type { QaAppAnalytics, QaDistribution, QaRunAnalytics } from './types';

function DistributionCells({ dist }: { dist: QaDistribution }) {
  return (
    <>
      <td className="px-4 py-2.5 text-slate-600">{formatSeconds(dist.p50)}</td>
      <td className="px-4 py-2.5 text-slate-600">{formatSeconds(dist.p90)}</td>
      <td className="px-4 py-2.5 text-slate-600">{formatSeconds(dist.p99)}</td>
      <td className="px-4 py-2.5 text-slate-600">{formatSeconds(dist.max)}</td>
    </>
  );
}

function ApplicationTable({ applications }: { applications: QaAppAnalytics[] }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900">By application</h4>
        <p className="text-xs text-slate-500">
          Durations are per scan, from the moment the job started to the moment it finished.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
            <th className="px-4 py-2">Application</th>
            <th className="px-4 py-2">Done</th>
            <th className="px-4 py-2">Failed</th>
            <th className="px-4 py-2">Success</th>
            <th className="px-4 py-2">Cost</th>
            <th className="px-4 py-2">p50</th>
            <th className="px-4 py-2">p90</th>
            <th className="px-4 py-2">p99</th>
            <th className="px-4 py-2">Slowest</th>
          </tr>
        </thead>
        <tbody>
          {applications.map((app) => (
            <tr key={app.app} className="border-b border-slate-50 last:border-0">
              <td className="px-4 py-2.5">
                <span className="font-medium text-slate-900">{app.label}</span>
                {app.submit_error && (
                  <span className="block text-xs text-danger-700">{app.submit_error}</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-slate-600">
                {formatCount(app.completed)}
                <span className="text-slate-400"> / {formatCount(app.planned)}</span>
              </td>
              <td className="px-4 py-2.5 text-slate-600">
                {formatCount(app.failed + app.failed_to_submit)}
              </td>
              <td className="px-4 py-2.5 text-slate-600">
                {app.success_percentage == null ? '—' : `${app.success_percentage}%`}
              </td>
              <td className="px-4 py-2.5 text-slate-600 tabular-nums">
                {formatUsd(app.cost_usd)}
              </td>
              <DistributionCells dist={app.duration_seconds} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BatchId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={`${id} — click to copy`}
      onClick={() => {
        void navigator.clipboard.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="font-mono text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-700 transition-colors"
    >
      {copied ? 'copied' : id}
    </button>
  );
}

/**
 * The batches this run created, per application.
 *
 * These are what to search for in the application's own history to get from a QA
 * result to the scans behind it. Only a real-spend run appears here, and with a
 * single batch: a dummy run submits scan by scan and so creates none, and
 * gen-api's load is individual calls correlated by the run id.
 */
function BatchIdsPanel({ applications }: { applications: QaAppAnalytics[] }) {
  const rows = applications.filter((app) => (app.batch_ids || []).length > 0);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900">Batches</h4>
        <p className="text-xs text-slate-500">
          Look these up in the application's own history to reach the scans.
        </p>
      </div>
      <div className="divide-y divide-slate-50">
        {rows.map((app) => (
          <div key={app.app} className="px-4 py-2.5 flex items-start gap-3">
            <span className="text-sm text-slate-600 w-28 shrink-0 pt-0.5">{app.label}</span>
            <div className="flex flex-wrap gap-1.5">
              {app.batch_ids.map((id) => (
                <BatchId key={id} id={id} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SlowestTable({ applications }: { applications: QaAppAnalytics[] }) {
  const rows = applications
    .flatMap((app) => app.slowest.map((job) => ({ ...job, label: app.label })))
    .sort((a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0))
    .slice(0, 15);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900">Slowest scans</h4>
        <p className="text-xs text-slate-500">
          Across every application, worst first. Queue wait is time spent waiting for a worker.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
            <th className="px-4 py-2">Application</th>
            <th className="px-4 py-2">Scan</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Duration</th>
            <th className="px-4 py-2">Queue wait</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((job, index) => (
            <tr key={`${job.job_id || index}`} className="border-b border-slate-50 last:border-0">
              <td className="px-4 py-2.5 text-slate-600">{job.label}</td>
              <td className="px-4 py-2.5 font-mono text-xs text-slate-500">
                {job.scan_id || job.job_id || '—'}
              </td>
              <td className="px-4 py-2.5 text-slate-600">{job.status || '—'}</td>
              <td className="px-4 py-2.5 text-slate-900 font-medium">
                {formatSeconds(job.duration_seconds)}
              </td>
              <td className="px-4 py-2.5 text-slate-600">{formatSeconds(job.queue_seconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErrorTable({ applications }: { applications: QaAppAnalytics[] }) {
  const rows = applications.flatMap((app) =>
    app.errors.map((group) => ({ ...group, label: app.label })),
  );
  if (rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900">Failures</h4>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
            <th className="px-4 py-2">Application</th>
            <th className="px-4 py-2">Error</th>
            <th className="px-4 py-2">Count</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.label}-${index}`} className="border-b border-slate-50 last:border-0">
              <td className="px-4 py-2.5 text-slate-600">{row.label}</td>
              <td className="px-4 py-2.5 text-slate-700">{row.error}</td>
              <td className="px-4 py-2.5 text-slate-900 font-medium">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every scan that failed, one row each.
 *
 * The grouped table above says what went wrong and how often; this says which
 * scan it happened to, which is the only version you can act on. Scans rejected
 * at submission never got a job row, so they are counted in the header rather
 * than listed.
 */
function FailedScansPanel({ applications }: { applications: QaAppAnalytics[] }) {
  const rows = applications.flatMap((app) =>
    (app.failed_jobs || []).map((job) => ({ ...job, label: app.label })),
  );
  const rejected = applications.reduce((sum, app) => sum + app.failed_to_submit, 0);
  if (rows.length === 0 && rejected === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100">
        <h4 className="text-sm font-semibold text-slate-900">Failed scans</h4>
        <p className="text-xs text-slate-500">
          {rows.length > 0
            ? `${formatCount(rows.length)} scan${rows.length === 1 ? '' : 's'} failed after being accepted.`
            : 'No accepted scan failed.'}
          {rejected > 0 &&
            ` A further ${formatCount(rejected)} were rejected at submission and never got a job.`}
        </p>
      </div>
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-slate-500 border-b border-slate-100">
              <th className="px-4 py-2">Application</th>
              <th className="px-4 py-2">Scan</th>
              <th className="px-4 py-2">Job</th>
              <th className="px-4 py-2">Error</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((job, index) => (
              <tr
                key={`${job.job_id || index}`}
                className="border-b border-slate-50 last:border-0 align-top"
              >
                <td className="px-4 py-2.5 text-slate-600">{job.label}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">
                  {job.scan_id || '—'}
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-400">
                  {job.job_id || '—'}
                </td>
                <td className="px-4 py-2.5 text-slate-700">{job.error || 'No error recorded'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * One run's full breakdown, loaded on demand when its history row is expanded.
 *
 * The gen-api section reuses the gen-api stress charts unchanged: the backend
 * returns that application's analytics in exactly the shape those charts expect.
 */
export function RunBreakdown({ runId }: { runId: string }) {
  const [analytics, setAnalytics] = useState<QaRunAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAnalytics(null);
    setError(null);
    fetchQaRunAnalytics(runId)
      .then((data) => {
        if (!cancelled) setAnalytics(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (error) {
    return (
      <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-3 py-2 text-sm">
        {error}
      </div>
    );
  }
  if (!analytics) {
    return <div className="text-sm text-slate-500">Loading breakdown…</div>;
  }

  const { run, applications, genapi } = analytics;
  const totals = applications.reduce(
    (acc, app) => ({
      planned: acc.planned + app.planned,
      completed: acc.completed + app.completed,
      failed: acc.failed + app.failed + app.failed_to_submit,
    }),
    { planned: 0, completed: 0, failed: 0 },
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(
          [
            [
              'Total test time',
              formatSeconds(analytics.wall_seconds),
              'Start to the last scan finishing — not just how long submitting took.',
            ],
            ['Planned', formatCount(run.calls_planned || totals.planned), undefined],
            ['Completed', formatCount(totals.completed + (genapi?.totals.success || 0)), undefined],
            [
              'Failed',
              formatCount(
                totals.failed + (genapi?.totals.errors || 0) + run.calls_failed_to_submit,
              ),
              undefined,
            ],
            [
              'Total cost',
              runCostLabel(analytics.cost),
              analytics.cost?.final
                ? 'Final: every scan and call has finished.'
                : 'Spend so far — work submitted by this run is still generating.',
            ],
          ] as const
        ).map(([label, value, hint]) => (
          <div
            key={label}
            title={hint}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3"
          >
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-lg font-semibold text-slate-900">{value}</div>
          </div>
        ))}
      </div>

      {applications.length > 0 && <ApplicationTable applications={applications} />}
      {applications.length > 0 && <BatchIdsPanel applications={applications} />}
      {applications.length > 0 && <SlowestTable applications={applications} />}
      {applications.length > 0 && <ErrorTable applications={applications} />}
      {applications.length > 0 && <FailedScansPanel applications={applications} />}

      {genapi && (
        <div className="space-y-4">
          {/* Gen-api is absent from the application table above — its load is
              calls, not batched scans — so its spend is stated here. */}
          <h4 className="text-sm font-semibold text-slate-900">
            {genapi.label}
            <span className="ml-2 font-normal text-xs text-slate-500">
              {formatUsd(genapi.cost_usd)}
            </span>
          </h4>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ThroughputChart analytics={genapi} />
            <LatencyOverTimeChart analytics={genapi} />
            <PercentileChart analytics={genapi} />
            <OutcomeChart analytics={genapi} />
          </div>
          <ModelBreakdownTable analytics={genapi} />
        </div>
      )}

      {applications.length === 0 && !genapi && (
        <p className="text-sm text-slate-500">
          This run has no per-application data — it was cancelled or failed before submitting
          anything.
        </p>
      )}
    </div>
  );
}
